import { createServerFn } from '@tanstack/react-start';
import { count, gte, like, sql } from 'drizzle-orm';
import { getDaemonHeartbeat, getLastScanRun } from '@/db/models';
import { applications, jobs } from '@/db/schema';
import { getDb } from '@/lib/db';
import { authMiddleware } from '@/lib/server-fn-auth';

interface DaemonStatusResult {
  lastHeartbeatAt: string | null;
  mode: 'offpeak' | 'peak' | 'weekend' | null;
  nextScanAt: string | null;
  pid: number | null;
  running: boolean;
  schedule: { offpeakMin: number; peakMin: number } | null;
  stale: boolean;
  startedAt: string | null;
  stoppedAt: string | null;
  uptimeMs: number | null;
}

interface LastScanResult {
  durationMs: number | null;
  errorsCount: number;
  finishedAt: string | null;
  id: number;
  jobsDeduped: number;
  jobsNew: number;
  perSource: Record<
    string,
    { errors: string[]; new: number; total: number }
  > | null;
  scoredAttempted: number;
  scoredSucceeded: number;
  startedAt: string;
  tailoredEligible: number;
  tailoredSucceeded: number;
  triggerSource: 'daemon' | 'manual';
}

/** Dashboard counters. Mirrors `GET /api/stats`. */
export const fetchStats = createServerFn({ method: 'GET' })
  .middleware([authMiddleware])
  .handler(async () => {
    const db = getDb();

    // The four aggregates are independent — run them concurrently so the
    // most-polled endpoint pays one round-trip's latency, not four.
    const [skippedRows, jobCounts, appCounts, daily] = await Promise.all([
      db
        .select({ count: count() })
        .from(jobs)
        .where(like(jobs.match_reasoning, '%skip_reason%'))
        .limit(1),
      db
        .select({ count: count(), status: jobs.status })
        .from(jobs)
        .groupBy(jobs.status),
      db
        .select({ count: count(), status: applications.status })
        .from(applications)
        .groupBy(applications.status),
      db
        .select({
          count: count(),
          date: sql<string>`date_trunc('day', ${jobs.scraped_at})`,
        })
        .from(jobs)
        .where(gte(jobs.scraped_at, sql`NOW() - INTERVAL '7 days'`))
        .groupBy(sql`date_trunc('day', ${jobs.scraped_at})`)
        .orderBy(sql`date_trunc('day', ${jobs.scraped_at})`),
    ]);

    const skippedCount = skippedRows[0]?.count ?? 0;

    return {
      applications: {
        byStatus: Object.fromEntries(
          appCounts.map((row) => [row.status, row.count]),
        ),
        total: appCounts.reduce((sum, row) => sum + row.count, 0),
      },
      daily,
      jobs: {
        byStatus: Object.fromEntries(
          jobCounts.map((row) => [row.status, row.count]),
        ),
        skipped: skippedCount,
        total:
          jobCounts.reduce((sum, row) => sum + row.count, 0) - skippedCount,
      },
    };
  });

/** Daemon liveness. Mirrors `GET /api/internal/daemon/status`. */
export const fetchDaemonStatus = createServerFn({ method: 'GET' })
  .middleware([authMiddleware])
  .handler(async (): Promise<DaemonStatusResult> => {
    const db = getDb();
    const row = await getDaemonHeartbeat(db);

    if (!row) {
      return {
        lastHeartbeatAt: null,
        mode: null,
        nextScanAt: null,
        pid: null,
        running: false,
        schedule: null,
        stale: false,
        startedAt: null,
        stoppedAt: null,
        uptimeMs: null,
      };
    }

    const now = Date.now();
    const startedAtMs = new Date(row.started_at).getTime();
    const uptimeMs = Math.floor(now - startedAtMs);

    if (row.stopped_at) {
      return {
        lastHeartbeatAt: row.last_heartbeat_at,
        mode: row.mode as 'offpeak' | 'peak' | 'weekend',
        nextScanAt: row.next_scan_at ?? null,
        pid: row.pid,
        running: false,
        schedule: {
          offpeakMin: row.schedule_offpeak_min,
          peakMin: row.schedule_peak_min,
        },
        stale: false,
        startedAt: row.started_at,
        stoppedAt: row.stopped_at,
        uptimeMs,
      };
    }

    const lastHeartbeatMs = new Date(row.last_heartbeat_at).getTime();
    const staleThresholdMs =
      Math.max(row.schedule_peak_min, row.schedule_offpeak_min) * 60_000
      + 5 * 60_000;
    const stale = now - lastHeartbeatMs > staleThresholdMs;

    return {
      lastHeartbeatAt: row.last_heartbeat_at,
      mode: row.mode as 'offpeak' | 'peak' | 'weekend',
      nextScanAt: row.next_scan_at ?? null,
      pid: row.pid,
      running: !stale,
      schedule: {
        offpeakMin: row.schedule_offpeak_min,
        peakMin: row.schedule_peak_min,
      },
      stale,
      startedAt: row.started_at,
      stoppedAt: null,
      uptimeMs,
    };
  });

/** Most recent scan run. Mirrors `GET /api/internal/scan/last`. */
export const fetchLastScan = createServerFn({ method: 'GET' })
  .middleware([authMiddleware])
  .handler(async (): Promise<LastScanResult | null> => {
    const db = getDb();
    const row = await getLastScanRun(db);

    if (!row) {
      return null;
    }

    return {
      durationMs: row.duration_ms ?? null,
      errorsCount: row.errors_count,
      finishedAt: row.finished_at ?? null,
      id: row.id,
      jobsDeduped: row.jobs_deduped,
      jobsNew: row.jobs_new,
      perSource: row.per_source,
      scoredAttempted: row.scored_attempted,
      scoredSucceeded: row.scored_succeeded,
      startedAt: row.started_at,
      tailoredEligible: row.tailored_eligible,
      tailoredSucceeded: row.tailored_succeeded,
      triggerSource: row.trigger_source as 'daemon' | 'manual',
    };
  });
